import React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ComputerIcon from '@mui/icons-material/Computer';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Box, Button, Chip, Grid, Link, Stack, Typography } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import {
  EmptyHint,
  PagedWidgetList,
  StatTile,
  Widget,
  WidgetRow,
} from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { ScreenKey } from 'constant/ScreenPermission';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import {
  SecurityCheckListResult,
  SecurityCheckResultListItem,
  listSecurityCheckAdmin,
  listSecurityCheckResult,
} from 'stores/SecurityCheckStoreAdapter';
import { clampPage } from 'utils/UserProfileGuard';

const ACCENT = '#2e7d32';

/**
 * セキュリティドメインのトップ（ヒーロー + ウィジェットダッシュボード）。
 * 「自分の未実施チェック」を最優先に据えた自分ごとダッシュボード。
 * - 自分の未実施セキュリティチェック（最優先アクション）
 * - KPI（未実施 / 実施済み）
 * - 機能ナビ（セキュリティチェック実施 / 管理コンソール）
 */
const SecurityHome: React.FC = () => {
  const navigate = useNavigate();
  const { userSession, financialYearData } = useAuth();
  const { allowedScreens } = userSession;
  const canManage = allowedScreens.includes(ScreenKey.SECURITY_CHECK_MANAGEMENT);

  // ID指定の詳細は必ず別タブで開く
  const openCheckInNewTab = (securityCheckResultId: string) => {
    window.open(
      `/security/security-check/${securityCheckResultId}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const [unresolved, setUnresolved] = React.useState<SecurityCheckResultListItem[]>([]);
  const [adminMonths, setAdminMonths] = React.useState<SecurityCheckListResult[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // ダッシュボードのリストページ状態を user_profile(AUTO) で保存/復元する
  const [dashboard, setDashboard] = useDashboardProfile('security-home-dashboard', {
    unresolvedPage: 0,
  });
  const setUnresolvedPage = React.useCallback(
    (p: number) => setDashboard((prev) => ({ ...prev, unresolvedPage: p })),
    [setDashboard],
  );

  const currentYear = financialYearData.current?.financialYear ?? null;
  const currentMonth = financialYearData.current?.month ?? null;

  // ウィジェットの元データ（自分の未実施 + 管理者は月次一覧）を取得する。初回ロードと更新ボタンで共用。
  const reload = React.useCallback(async () => {
    const tasks: Promise<unknown>[] = [
      listSecurityCheckResult({ includeCompleted: false })
        .then((open) => setUnresolved(open.filter((r) => r.resultCode === null)))
        .catch(() => setUnresolved([])),
    ];
    // 管理者は現在年度の実施状況（月次一覧）も取得し、実施開始漏れ検知に使う
    if (canManage && currentYear) {
      tasks.push(
        listSecurityCheckAdmin({ financialYear: currentYear })
          .then(setAdminMonths)
          .catch(() => setAdminMonths([])),
      );
    }
    try {
      await Promise.all(tasks);
    } finally {
      setLoading(false);
    }
  }, [canManage, currentYear]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // ヒーローの更新ボタン: 再取得中はスピナー表示にする
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  const monthlyPending = unresolved.filter((r) => r.securityCheckType === 'MONTHLY').length;
  const pcPending = unresolved.filter((r) => r.securityCheckType === 'PC_PATCH').length;

  // 実施開始済み（NOT_STARTED 以外）の最新月と、当月の開始状況
  const startedMonths = adminMonths.filter(
    (m) => m.monthlyStatus !== 'NOT_STARTED' || m.pcPatchStatus !== 'NOT_STARTED',
  );
  const latestStartedMonth =
    startedMonths.length > 0
      ? startedMonths.reduce((a, b) => (b.month > a.month ? b : a)).month
      : null;
  const currentMonthRow = adminMonths.find((m) => m.month === currentMonth) ?? null;
  const currentMonthStarted =
    currentMonthRow != null &&
    (currentMonthRow.monthlyStatus !== 'NOT_STARTED' ||
      currentMonthRow.pcPatchStatus !== 'NOT_STARTED');

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        {/* ヒーロー */}
        <DomainHero
          eyebrow="Security"
          title="Stay Secure, Stay Compliant"
          tagline="Run your monthly self-checks and keep your devices patched. Everything you need to stay on top of security lives here."
          palette={HERO_PALETTES.security}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          actions={
            canManage ? (
              <Button
                variant="contained"
                startIcon={<FactCheckIcon />}
                onClick={() => navigate('/security/security-check-management')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  boxShadow: 'none',
                  bgcolor: '#fff',
                  color: ACCENT,
                  '& .MuiButton-startIcon': { color: ACCENT },
                  '&:hover': { boxShadow: 'none', bgcolor: 'rgba(255,255,255,0.88)' },
                }}
              >
                管理コンソール
              </Button>
            ) : undefined
          }
        />

        {/* KPI（未実施のみ・月次/PCパッチ内訳）※最上部 */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <StatTile
              icon={<RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />}
              label="未実施（合計）"
              value={unresolved.length}
              suffix="件"
              color="#ed6c02"
              loading={loading}
              onClick={() => navigate('/security/security-check')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <StatTile
              icon={<VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />}
              label="月次（未実施）"
              value={monthlyPending}
              suffix="件"
              color={ACCENT}
              loading={loading}
              onClick={() => navigate('/security/security-check?securityCheckType=MONTHLY')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <StatTile
              icon={<ComputerIcon sx={{ fontSize: 16 }} />}
              label="PCパッチ（未実施）"
              value={pcPending}
              suffix="件"
              color="#1565c0"
              loading={loading}
              onClick={() => navigate('/security/security-check?securityCheckType=PC_PATCH')}
            />
          </Grid>
        </Grid>

        {/* 管理者向け: 当月の実施開始状況（実施開始漏れ防止） */}
        {canManage && !loading && currentYear && currentMonth && (
          <Box
            sx={(t) => ({
              borderRadius: 3,
              p: 2.5,
              border: 1,
              borderColor: currentMonthStarted ? 'divider' : 'warning.main',
              bgcolor: currentMonthStarted
                ? t.palette.mode === 'dark'
                  ? 'rgba(46,125,50,0.10)'
                  : 'rgba(46,125,50,0.05)'
                : t.palette.mode === 'dark'
                  ? 'rgba(237,108,2,0.14)'
                  : 'rgba(237,108,2,0.07)',
            })}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              flexWrap="wrap"
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                {currentMonthStarted ? (
                  <CheckCircleOutlineIcon sx={{ color: ACCENT }} />
                ) : (
                  <ReportProblemOutlinedIcon sx={{ color: 'warning.main' }} />
                )}
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      当月（{currentYear}期 {currentMonth}月）
                    </Typography>
                    <Chip
                      size="small"
                      label={currentMonthStarted ? '実施開始済み' : '未開始'}
                      color={currentMonthStarted ? 'success' : 'warning'}
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    実施開始済みの最新月:{' '}
                    {latestStartedMonth ? `${latestStartedMonth}月` : '（まだありません）'}
                    {!currentMonthStarted && ' ／ 当月のチェックがまだ開始されていません'}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant={currentMonthStarted ? 'outlined' : 'contained'}
                size="small"
                color={currentMonthStarted ? 'inherit' : 'warning'}
                startIcon={<FactCheckIcon />}
                onClick={() =>
                  navigate(
                    `/security/security-check-management?financialYear=${currentYear}&month=${currentMonth}`,
                  )
                }
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  flexShrink: 0,
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                {currentMonthStarted ? '当月を確認' : '当月を開始'}
              </Button>
            </Stack>
          </Box>
        )}

        {/* 自分の未実施チェック（最優先アクション） */}
        {!loading &&
          (unresolved.length === 0 ? (
            <Box
              sx={(t) => ({
                borderRadius: 3,
                p: 2.5,
                border: 1,
                borderColor: 'divider',
                bgcolor:
                  t.palette.mode === 'dark' ? 'rgba(46,125,50,0.14)' : 'rgba(46,125,50,0.06)',
              })}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CheckCircleOutlineIcon sx={{ color: ACCENT }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    未実施のセキュリティチェックはありません
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    お疲れ様です。すべてのチェックが実施済みです。
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ) : (
            <Widget
              title={`自分の未実施チェック（${unresolved.length}）`}
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/security/security-check')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  すべて見る
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              <PagedWidgetList
                items={unresolved}
                keyOf={(r) => r.securityCheckResultId}
                page={clampPage(dashboard.unresolvedPage + 1, Math.ceil(unresolved.length / 6)) - 1}
                onPageChange={setUnresolvedPage}
                renderItem={(r) => (
                  <WidgetRow
                    accentColor={ACCENT}
                    onClick={() => openCheckInNewTab(r.securityCheckResultId)}
                  >
                    {r.securityCheckType === 'PC_PATCH' ? (
                      <ComputerIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
                    ) : (
                      <VerifiedUserOutlinedIcon
                        sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {r.title}
                      </Typography>
                      {r.assessmentName && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {r.assessmentName}
                          {r.managementNumber1 ? `  ${r.managementNumber1}` : ''}
                        </Typography>
                      )}
                    </Box>
                    <RadioButtonUncheckedIcon
                      sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }}
                    />
                  </WidgetRow>
                )}
              />
            </Widget>
          ))}

        {!loading && unresolved.length === 0 && (
          <EmptyHint text="セキュリティチェックはまだ開始されていません。" />
        )}
      </Stack>
    </Box>
  );
};

export default SecurityHome;
